import { HugeiconsIcon } from "@hugeicons/react";
import { Logout01Icon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { Link } from "react-router";

import { Tooltip, TooltipTrigger, TooltipPopup } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { asset } from "@/lib/asset";

export interface RailNavItem {
  title: string;
  href: string;
  icon: IconSvgElement;
  active?: boolean;
  badge?: React.ReactNode;
}

export interface RailSection {
  label?: string;
  items: RailNavItem[];
}

interface SimpleSidebarProps {
  sections: RailSection[];
  footerItems?: RailNavItem[];
  className?: string;
  onLogout?: () => void;
  logoutLabel?: string;
  /** Icon-rail (w-16) when true; labelled (w-64) when false. */
  collapsed?: boolean;
  /** Role tag shown in the primary colour after the wordmark. */
  roleLabel?: string;
  /** Wordmark name, e.g. "Prado". */
  appName?: string;
  /** Slot between the logo and the nav (e.g. a workspace switcher). */
  topSlot?: React.ReactNode;
  /** Called after any navigation click (closes the mobile drawer). */
  onNavigate?: () => void;
}

/**
 * Desktop sidebar — flat ticketbalie-style list. Fixed w-64 labelled rail or
 * w-16 icon rail with tooltips; logout lives at the bottom with a destructive
 * hover. Pure presentational; nav data stays in `AppSidebar`.
 */
export function SimpleSidebar({
  sections,
  footerItems = [],
  className,
  onLogout,
  logoutLabel = "Uitloggen",
  collapsed = false,
  roleLabel,
  appName = "Prado",
  topSlot,
  onNavigate,
}: SimpleSidebarProps) {
  const rowClass = (active?: boolean) =>
    cn(
      "flex h-10 w-full items-center rounded-lg transition-colors",
      collapsed ? "justify-center" : "gap-3 px-3",
      active
        ? "bg-primary/10 font-medium text-primary"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
    );

  const renderItem = (item: RailNavItem) => {
    const Icon = item.icon;
    const link = (
      <Link
        to={item.href}
        onClick={onNavigate}
        aria-label={item.title}
        aria-current={item.active ? "page" : undefined}
        className={rowClass(item.active)}
      >
        <HugeiconsIcon icon={Icon} size={20} className="shrink-0" />
        {!collapsed && (
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.title}</span>
        )}
        {!collapsed && item.badge}
      </Link>
    );
    if (!collapsed) return <div key={item.href}>{link}</div>;
    return (
      <Tooltip key={item.href}>
        <TooltipTrigger render={link} />
        <TooltipPopup side="right">{item.title}</TooltipPopup>
      </Tooltip>
    );
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-border bg-card",
        collapsed ? "w-16" : "w-64",
        className,
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-border",
          collapsed ? "justify-center px-2" : "gap-2.5 px-4",
        )}
      >
        <img
          src={asset("favicon.png")}
          alt={appName}
          className="h-8 w-8 shrink-0 rounded-lg border border-border/60 bg-card object-contain p-1"
        />
        {!collapsed && (
          <span className="font-heading truncate text-sm font-bold tracking-tight text-foreground">
            {appName}
            {roleLabel && <span className="text-primary"> {roleLabel}</span>}
          </span>
        )}
      </div>

      {/* Optional slot between logo and nav (workspace switcher) */}
      {topSlot && <div className="shrink-0 border-b border-border px-2 py-2">{topSlot}</div>}

      {/* Navigation — flat ticketbalie-style list; groups are separated by
          hairline dividers, no subtitles. */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {sections.map((section, i) => (
          <div key={section.label ?? i} className={i > 0 ? "mt-3 border-t border-border pt-3" : ""}>
            <div className="space-y-0.5">{section.items.map(renderItem)}</div>
          </div>
        ))}
      </nav>

      {/* Footer: settings etc. + logout */}
      {(footerItems.length > 0 || onLogout) && (
        <div className="shrink-0 border-t border-border px-2 py-2">
          {footerItems.map(renderItem)}
          {onLogout &&
            (collapsed ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      onClick={onLogout}
                      aria-label={logoutLabel}
                      className="flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <HugeiconsIcon icon={Logout01Icon} size={20} />
                    </button>
                  }
                />
                <TooltipPopup side="right">{logoutLabel}</TooltipPopup>
              </Tooltip>
            ) : (
              <button
                onClick={onLogout}
                className="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              >
                <HugeiconsIcon icon={Logout01Icon} size={20} />
                <span className="truncate text-sm font-medium">{logoutLabel}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
